import io from 'socket.io-client';
import feathers from '@feathersjs/client/dist/feathers.min.js';
import socketio from '@feathersjs/client/dist/socketio.min.js';
import auth from '@feathersjs/client/dist/authentication.min.js';
import config from '@/config';
import Vue from 'vue';

const socket = io(config.server_url);
const signageSocket = io(config.server_url, { path: '/signage' });

export const client = feathers();
export const conversionEventBus = new Vue();

let _currentUserId = null;
let _sessionHandler = null;

try {
  client
    .configure(socketio(socket, { timeout: config.socket_timeout }))
    .configure(auth({ storage: window.localStorage }));

  client.io = socket;
  client.signageSocket = signageSocket;
} catch (error) {
  console.error('Error configuring Feathers client:', error);
}

// ─── Socket lifecycle ─────────────────────────────────────────────────────────

socket.on('connect', () => setupConversionListeners());
socket.on('reconnect', () => {
  setupConversionListeners();
  reregisterSessionHandler();
});

signageSocket.on('connect', () => {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
});
signageSocket.on('reconnect', () => {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
});

if (socket.connected) setupConversionListeners();
if (signageSocket.connected) {
  setupDeviceLogsListeners();
  setupDownloadProgressListeners();
}

// Support Socket.IO v3+ reconnect event
if (socket.io) {
  socket.io.on('reconnect', () => reregisterSessionHandler());
}

// ─── Conversion listeners ─────────────────────────────────────────────────────

function setupConversionListeners() {
  socket.off('conversion:start');
  socket.off('conversion:progress');
  socket.off('conversion:complete');
  socket.off('conversion:error');

  socket.on('conversion:start', data => {
    conversionEventBus.$emit('conversion:start', {
      id: `conversion_${data.filename}`,
      name: `Converting ${data.originalFileName || data.filename}`,
      message: 'Converting H.265 to H.264...'
    });
  });

  socket.on('conversion:progress', data => {
    conversionEventBus.$emit('conversion:progress', {
      id: `conversion_${data.filename}`,
      progress: data.progress,
      name: data.originalFileName || data.filename
    });
  });

  socket.on('conversion:complete', data => {
    conversionEventBus.$emit('conversion:complete', {
      id: `conversion_${data.filename}`,
      message: `${data.originalFileName || data.filename} converted successfully!`
    });
  });

  socket.on('conversion:error', data => {
    conversionEventBus.$emit('conversion:error', {
      id: `conversion_${data.filename}`,
      message: `Failed to convert ${data.originalFileName || data.filename}: ${data.error}`
    });
  });
}

// ─── Device logs listeners ────────────────────────────────────────────────────

function setupDeviceLogsListeners() {
  signageSocket.off('device-logs:requesting');
  signageSocket.off('device-logs:ready');
  signageSocket.off('device-logs:error');

  signageSocket.on('device-logs:requesting', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: true
      });
    });
  });

  signageSocket.on('device-logs:ready', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: false
      });
    });

    let downloadUrl = data.downloadUrl;
    if (!downloadUrl.startsWith('http')) {
      downloadUrl = window.location.origin + downloadUrl;
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = data.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 100);

    conversionEventBus.$emit('show-notification', {
      type: 'success',
      message: `Logs from ${data.deviceName} downloaded successfully!`
    });
  });

  signageSocket.on('device-logs:error', data => {
    import('@/store').then(({ default: store }) => {
      store.commit('devices/SET_DOWNLOADING_LOGS', {
        deviceId: data.deviceId,
        isDownloading: false
      });
    });

    conversionEventBus.$emit('show-notification', {
      type: 'error',
      message: data.error || 'Failed to download logs'
    });
  });
}

// ─── Download progress listeners ──────────────────────────────────────────────

function setupDownloadProgressListeners() {
  signageSocket.off('device-download-progress');
  signageSocket.off('active-downloads-snapshot');

  signageSocket.on('device-download-progress', data => {
    conversionEventBus.$emit('device-download-progress', data);
  });

  // Receive snapshot of downloads already in progress before this browser connected
  signageSocket.on('active-downloads-snapshot', snapshot => {
    conversionEventBus.$emit('active-downloads-snapshot', snapshot);
  });

  // Request current active downloads from server
  signageSocket.emit('get-active-downloads');
}

// ─── Session invalidation handler ────────────────────────────────────────────

function reregisterSessionHandler() {
  if (_currentUserId && _sessionHandler) {
    socket.off('user-session-invalidated', _sessionHandler);
    socket.on('user-session-invalidated', _sessionHandler);
  }
}

export function registerSessionInvalidationHandler(userId, onInvalidated) {
  _currentUserId = String(userId);

  // Remove previous handler if any
  if (_sessionHandler) {
    socket.off('user-session-invalidated', _sessionHandler);
  }

  _sessionHandler = function(data) {
    if (String(data.userId) === _currentUserId) {
      onInvalidated(data);
    }
  };

  socket.on('user-session-invalidated', _sessionHandler);
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export function getSignageSocketId() {
  return signageSocket && signageSocket.id ? signageSocket.id : null;
}

export { socket, signageSocket };



// WEBPACK FOOTER //
// ./src/services/index.js